import Papa from 'papaparse';

import type { CppPartner } from '~/types/costPerPoint';
import type { AdminPartnerTransaction, PartnerEarnCsvRow, PartnerEarnRowResult } from '~/types/loyaltyContract';

import { MAX_PARTNER_EARN_CSV_ROWS, parseOptionalInteger } from './partnerEarnCredit';

export const PARTNER_EARN_CSV_HEADERS = [
  'account_number',
  'base_points',
  'status_credits',
  'activity_title',
  'reason',
  'source_key',
] as const;

interface ParsedPartnerEarnCsvRecord {
  account_number?: string;
  base_points?: string;
  status_credits?: string;
  activity_title?: string;
  reason?: string;
  source_key?: string;
}

interface CsvValidationResult {
  rows: Array<PartnerEarnCsvRow>;
  errors: Array<string>;
  warnings: Array<string>;
}

/**
 * Saves `csvContent` as `filename`.
 *
 * The anchor is attached before clicking and the object URL is revoked on a later tick: a download
 * is asynchronous, and revoking synchronously after `click()` races it, which makes the browser
 * fall back to naming the file after the blob's UUID with no extension.
 */
function downloadCsv(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function downloadPartnerEarnTemplate(): void {
  const csvContent = Papa.unparse({ fields: [...PARTNER_EARN_CSV_HEADERS], data: [] });
  downloadCsv(csvContent, 'partner_earn_template.csv');
}

export function downloadFailedPartnerEarnCsv(failures: Array<PartnerEarnRowResult>): void {
  const headers = [...PARTNER_EARN_CSV_HEADERS, 'error'];
  const rows = failures.map((failure) => ({
    account_number: failure.accountNumber,
    base_points: failure.basePoints ?? '',
    status_credits: failure.statusCredits ?? '',
    activity_title: failure.activityTitle ?? '',
    reason: failure.reason ?? '',
    source_key: failure.sourceKey ?? '',
    error: failure.error ?? 'Unknown error',
  }));

  const csvContent = Papa.unparse(rows, { columns: headers });
  downloadCsv(csvContent, `partner_earn_failures_${Date.now()}.csv`);
}

/**
 * Exports credits that were accepted but then failed downstream.
 *
 * Distinct from the submission error report: these rows reached the service and hold a transaction
 * id, so they carry IBS's own `errorMessage` alongside the vaguer `failureReason` an operator
 * cannot act on. Exported in full rather than the handful the screen lists inline.
 */
export function downloadProcessingFailuresCsv(failures: Array<AdminPartnerTransaction>): void {
  const rows = failures.map((failure) => ({
    account_number: failure.accountNumber,
    base_points: failure.basePoints ?? '',
    status_credits: failure.statusCredits ?? '',
    activity_title: failure.activityTitle,
    reason: failure.reason ?? '',
    source_key: failure.sourceKey ?? '',
    transaction_id: failure.transactionId,
    failure_reason: failure.failureReason ?? '',
    error_message: failure.errorMessage ?? '',
    created_at: failure.createdAt,
    processed_at: failure.processedAt ?? '',
  }));

  const csvContent = Papa.unparse(rows, {
    columns: [
      ...PARTNER_EARN_CSV_HEADERS,
      'transaction_id',
      'failure_reason',
      'error_message',
      'created_at',
      'processed_at',
    ],
  });
  downloadCsv(csvContent, `partner_earn_processing_failures_${Date.now()}.csv`);
}

export function parsePartnerEarnCsv(fileContent: string): {
  data: Array<ParsedPartnerEarnCsvRecord>;
  parseErrors: Array<string>;
} {
  const { data, errors } = Papa.parse<ParsedPartnerEarnCsvRecord>(fileContent, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.toLowerCase().trim(),
  });

  const parseErrors = errors.map((err) => `Row ${(err.row ?? 0) + 1}: ${err.message}`);
  return { data, parseErrors };
}

export function validatePartnerEarnCsvRows(
  data: Array<ParsedPartnerEarnCsvRecord>,
  partner: CppPartner,
): CsvValidationResult {
  const errors: Array<string> = [];
  const warnings: Array<string> = [];
  const rows: Array<PartnerEarnCsvRow> = [];
  const seenAccountNumbers = new Set<number>();

  if (data.length === 0) {
    errors.push('CSV file is empty');
    return { rows, errors, warnings };
  }

  if (data.length > MAX_PARTNER_EARN_CSV_ROWS) {
    errors.push(`CSV exceeds the ${MAX_PARTNER_EARN_CSV_ROWS}-row limit (${data.length} rows)`);
    return { rows, errors, warnings };
  }

  data.forEach((record, index) => {
    const rowNum = index + 2;
    const accountParsed = parseOptionalInteger(record.account_number ?? '');
    const baseParsed = parseOptionalInteger(record.base_points ?? '');
    const statusParsed = parseOptionalInteger(record.status_credits ?? '');
    const activityTitle = record.activity_title?.trim() ?? '';
    const reason = record.reason?.trim() ?? '';
    const sourceKey = record.source_key?.trim();
    const rowErrors: Array<string> = [];

    if (accountParsed.kind !== 'value' || accountParsed.value < 1) {
      rowErrors.push('account_number must be a positive whole number');
    }

    if (baseParsed.kind === 'invalid') {
      rowErrors.push('base_points must be a whole number');
    } else if (baseParsed.kind === 'value') {
      if (baseParsed.value < partner.minPointsPerTransaction || baseParsed.value > partner.maxPointsPerTransaction) {
        rowErrors.push(
          `base_points must be between ${partner.minPointsPerTransaction.toLocaleString('en-AU')} and ${partner.maxPointsPerTransaction.toLocaleString('en-AU')}`,
        );
      }
    }

    if (statusParsed.kind === 'invalid') {
      rowErrors.push('status_credits must be a whole number');
    } else if (statusParsed.kind === 'value') {
      if (!partner.allowCreditStatusCredits) {
        rowErrors.push('status credits are not allowed for this partner');
      } else if (
        statusParsed.value < partner.minStatusCreditsPerTransaction ||
        statusParsed.value > partner.maxStatusCreditsPerTransaction
      ) {
        rowErrors.push(
          `status_credits must be between ${partner.minStatusCreditsPerTransaction.toLocaleString('en-AU')} and ${partner.maxStatusCreditsPerTransaction.toLocaleString('en-AU')}`,
        );
      }
    }

    if (!activityTitle) {
      rowErrors.push('activity_title is required');
    }

    if (!reason) {
      rowErrors.push('reason is required');
    }

    const hasBase = baseParsed.kind === 'value';
    const hasStatus = statusParsed.kind === 'value';
    if (!hasBase && !hasStatus) {
      rowErrors.push(
        partner.allowCreditStatusCredits ? 'base_points or status_credits is required' : 'base_points is required',
      );
    }

    if (rowErrors.length > 0) {
      errors.push(`Row ${rowNum}: ${rowErrors.join('; ')}`);
      return;
    }

    if (accountParsed.kind !== 'value') {
      return;
    }

    if (seenAccountNumbers.has(accountParsed.value)) {
      warnings.push(`Row ${rowNum}: duplicate account_number "${accountParsed.value}"`);
    }
    seenAccountNumbers.add(accountParsed.value);

    rows.push({
      rowIndex: rowNum,
      accountNumber: accountParsed.value,
      activityTitle,
      reason,
      ...(hasBase && baseParsed.kind === 'value' ? { basePoints: baseParsed.value } : {}),
      ...(hasStatus && statusParsed.kind === 'value' ? { statusCredits: statusParsed.value } : {}),
      ...(sourceKey ? { sourceKey } : {}),
    });
  });

  return { rows, errors, warnings };
}
